// The public read API: contracts/public.openapi.yaml.
//
// # Hand-written, and that is a decision
//
// tasks.md T272 says "generate". This is written by hand instead, for the same
// reason internal/httpapi's contract test parses the YAML rather than importing
// an OpenAPI library: the constitution's dependency rule is explicit, and the
// simplest option that meets the requirement wins.
//
// The stronger reason is accuracy. A generator would produce exactly what the
// contract declares, and the contract is a specification rather than a
// transcript — it omits fields the server adds for a human reader
// (`display_name` beside every `player_id`, so a view need not join the roster
// to show a name) and describes some it shapes differently. Generated types
// would therefore be confidently wrong in both directions, and the confidence
// is the dangerous half.
//
// **These types are transcribed from the responses the server actually
// returns.** `internal/httpapi/fixtures_test.go` captures those responses into
// `web/tests/fixtures/`, and the Playwright specs serve them, so a shape that
// drifts from this file breaks a browser test rather than a user's page.
//
// # Every money-like number is a string
//
// Points, handicap indexes, net scores and stroke allocations all arrive as
// decimal STRINGS, because the server computes them with exact decimal
// arithmetic (Constitution III) and JSON numbers are binary floating point.
// Parsing one into a JavaScript number here would reintroduce, in the browser,
// exactly the error the whole scoring core is built to avoid. They are
// displayed as they arrive and compared as strings or by the server.

/** A decimal value, carried as a string. Never parse one to display it. */
export type Decimal = string

export interface Season {
  id: number
  name: string
  /** Derived from the schedule -- the first and last week. Null until there is one. */
  start_date: string | null
  end_date: string | null
  is_current: boolean
}

export interface EventType {
  key: string
  display_name: string
  format: 'match_play' | 'stroke_play'
  /** Whether this type is a major, which is what records a winner and a runner-up (FR-036l). */
  is_major: boolean
}

/**
 * An event's state (FR-059).
 *
 * `provisional` is SCORED BUT NOT FINAL (FR-065): the standings have moved and
 * the week still accepts holes, because an event is played over a window rather
 * than an evening (FR-064). A reader must be able to tell one from `complete`,
 * which is why it is a state rather than a flag (FR-071).
 */
export type EventState = 'scheduled' | 'in_progress' | 'paused' | 'provisional' | 'complete'

export interface OpenEvent {
  event_id: number
  season_id: number
  season_name: string
  week_number: number
  scheduled_date: string
  course: string
  tee: string
  track: string
  holes_expected: number
  state: EventState
  /** The one the simulator is pointed at — where a submission naming no event lands (FR-075). */
  in_play: boolean
  /** False while paused: open, but completing nothing (FR-054). */
  accepting: boolean
}

/** A player a scored week is still waiting for (FR-071b). */
export interface Awaiting {
  player_id: string
  display_name: string
  /** How much of their card is in. "4 of 9" reads very differently from "0 of 9". */
  holes: number
  holes_expected: number
}

export interface EventSummary {
  id: number
  week_number: number
  scheduled_date: string
  course: string
  tee: string
  event_type: EventType
  /**
   * What the week was SCHEDULED with, rather than how it reads — so the console
   * can load a week back into the form it was created in. `course_data_id` is
   * null where no venue is decided yet; `track` is empty for an eighteen.
   */
  course_data_id: number | null
  track: string
  holes_expected: number
  /** Par of the holes this night plays — 36 for a nine, 72 for an eighteen. 0 with no venue. */
  par: number
  /**
   * The one night recorded with each player on their own nine (2024-25 week 9).
   * `par` is then a nine's par when both nines share it, and 0 when they do not.
   */
  each_plays_own_nine: boolean
  /** Visible wherever an event appears (FR-059). */
  state: EventState
  /** Who won a COMPLETED night, with the scores that won it. Empty otherwise. */
  winners: Winner[]
  /**
   * Who a SCORED week is still waiting for (FR-071b). Absent for any other
   * state. The badge says a week may move; this says why.
   */
  awaiting?: Awaiting[]
}

/**
 * A night's winner. `label` says what was won: first place in a stroke-play
 * field, the low net on a match-play week, or a playoff match.
 */
export interface Winner {
  player_id: string
  display_name: string
  label: 'winner' | 'low_net' | 'match_won' | 'match_forfeit'
  tied: boolean
  gross: number | null
  gross_to_par: number | null
  net: Decimal | null
  net_to_par: Decimal | null
  /**
   * Who they beat and by how much. Set only for `match_won`, because a match is
   * a pair rather than a winner: "Steve" says less than "Steve beat Dad 2&1".
   */
  opponent_display_name?: string
  margin?: string
}

export interface StandingRow {
  position: number
  player_id: string
  display_name: string
  matches_won: number
  matches_lost: number
  matches_halved: number
  points: Decimal
}

export interface Standings {
  official_points_source: string
  /**
   * How many of a player's rounds reach the total, when the season counts only
   * their best N (FR-036j). Absent when every round counts.
   *
   * Sent so the table can be checked: a member who adds up the nights and gets
   * a bigger number than the standing shows has found a rule, not a bug — but
   * only if the page says so.
   */
  counting_rounds?: number
  /** False for a season with no match play; its W/L/H columns are left out. */
  has_matches: boolean
  /** How many rounds are on the schedule, sent alongside `counting_rounds`. */
  scheduled_rounds?: number
  committed_through_event_id: number | null
  rows: StandingRow[]
  remaining_schedule: EventSummary[]
  /** Who won a FINISHED season; null while it is still being played. */
  champion: Champion | null
}

/**
 * A season's winner. `playoff` means the final's winner, who need not have
 * topped the table (2025-26); `points` means alone at the top of it.
 */
export interface Champion {
  player_id: string
  display_name: string
  decided_by: 'playoff' | 'points'
  points: Decimal
  final?: { opponent_id: string; opponent_name: string; margin: string | null }
}

/** Where a stroke falls, and the rank position that put it there (FR-008-F). */
export interface StrokeAllocation {
  hole: number
  rank_order: number
  strokes: Decimal
}

/** What actually governed an event, stored at Start Round (FR-027b, FR-023d). */
export interface GoverningValues {
  player_id: string
  display_name: string
  handicap_index: Decimal
  /** May be negative for a player below scratch on an easy course (FR-023b). */
  course_handicap: number
  allowance_pct: Decimal
  playing_handicap: Decimal
  handicap_adjustment: Decimal
  adjusted_playing_handicap: Decimal
  /** The commissioner replaced the computed playing handicap (FR-008-L). */
  playing_handicap_overridden: boolean
  /**
   * Strokes actually received ON THE HOLES PLAYED. On a nine this is NOT the
   * adjusted playing handicap: the eighteen-hole figure is allocated across all
   * eighteen by rank and whatever falls on the nine played is what you get
   * (FR-008-H).
   */
  strokes_received: Decimal
  holes_played: number
  course_rating: Decimal
  slope_rating: number
  par: number
  stroke_allocation: StrokeAllocation[]
  derivation?: Record<string, unknown>
}

export type Provenance = 'ingested' | 'manual' | 'corrected'

export interface SupersededScore {
  gross: number
  net: Decimal
  provenance: Provenance
  played_sequence: number
  source_ref: string | null
}

export interface ScorecardHole {
  hole: number
  par: number
  stroke_index: number
  gross: number | null
  strokes_allocated: Decimal
  net: Decimal | null
  /** Manual and corrected scores are visibly distinguished (FR-015, FR-018e). */
  provenance: Provenance
  /** The hole was played more than once; the superseded score stays visible. */
  replayed: boolean
  superseded?: SupersededScore[]
  source_ref: string | null
  flagged: boolean
  flag_reason?: string
}

export interface PlayerScorecard {
  player_id: string
  display_name: string
  holes: ScorecardHole[]
  holes_assembled: number
  /** False when the assembled count is not what the event expects (FR-017e). */
  matches_expected: boolean
  /**
   * Totals, summed by the server. Net and strokes are decimals — the playoff
   * bonus produces a half — and JavaScript has no exact decimal type, so adding
   * them here would reintroduce the error the scoring core avoids.
   */
  gross_total: number
  net_total: Decimal
  strokes_total: Decimal
  par_total: number
  /** Each total against the par of the holes it covers; null until a hole is scored. */
  gross_to_par: number | null
  net_to_par: Decimal | null
}

export interface MatchHole {
  hole: number
  stroke_index: number
  a_net: Decimal | null
  b_net: Decimal | null
  /** `pending` while only one player has finished the hole (FR-035c). */
  outcome: 'a' | 'b' | 'halved' | 'pending'
  /** The running state after this hole, e.g. "AS" or "2UP A". */
  status_after: string
  /**
   * False for a hole the match never needed: once the lead exceeds the holes
   * remaining it is over, and later holes score nothing (FR-030). Distinct from
   * `pending`, which means a player has yet to hole out.
   */
  contested: boolean
}

export interface Match {
  id: number
  player_a_id: string
  player_b_id: string
  status: 'scheduled' | 'in_progress' | 'won' | 'halved' | 'forfeit' | 'rescheduled'
  winner_player_id: string | null
  margin: string | null
  settled_on_hole: number | null
  /** Set when the commissioner recorded the outcome rather than play (FR-031). */
  outcome_note: string | null
  /**
   * The match hole by hole, computed from the recorded card by the server's
   * match-play engine — never re-decided here. A hole is won by the lower NET.
   */
  holes: MatchHole[]
  /**
   * **The engine's verdict, and the one to render.** `status`, `margin` and
   * `winner_player_id` above are the stored row, unwritten until the week is
   * scored — so mid-round they read `in_progress` with a null margin, and a
   * page showing them printed the literal word `in_progress` where a reader
   * expects "Mike 2 up".
   */
  status_text: string
  settled: boolean
  holes_remaining: number
  player_a_name: string
  player_b_name: string
}

export interface EventResult {
  player_id: string
  result_type: string
  match_id: number | null
  value: Decimal | null
  finish_rank: number | null
  tied: boolean
}

/**
 * One player's line in a night's results table (built and ordered on the
 * server, because it sums decimal points).
 */
export interface Placing {
  player_id: string
  display_name: string
  /** Set on a stroke-play night; a match-play night has no field placings. */
  finish_rank: number | null
  tied: boolean
  /** Totals over the holes finished, each with its score against their par; null with none. */
  gross: number | null
  gross_to_par: number | null
  net: Decimal | null
  net_to_par: Decimal | null
  holes_completed: number
  /**
   * Strokes received on the holes played — the handicap the round was played
   * off. On a nine this is not the eighteen-hole course handicap.
   */
  playing_handicap: Decimal | null
  /** That handicap was the commissioner's, not the index's (FR-008-L). */
  playing_handicap_overridden: boolean
  points: Decimal
  /** What the night recorded beyond a place — match_won, low_net, bye, … */
  earned: string[]
}

export interface EventDetail extends EventSummary {
  matches: Match[]
  bye_player_id: string | null
  results: EventResult[]
  /** The results table, one row per player. Empty until the round is completed. */
  placings: Placing[]
  scorecards: PlayerScorecard[]
  governing_values: GoverningValues[]
  /**
   * When the round last actually CHANGED — the most recent hole recorded, not
   * when the response was served. Always present (FR-035e).
   */
  updated_at: string
  /**
   * Nothing has arrived for a while. Only ever true while the round is
   * `in_progress`: a pause completes nothing and a make-up may be weeks away.
   */
  stale: boolean
  /**
   * The field's RUNNING net while the round is being played, and empty once it
   * has been scored — `placings` carries the committed answer then. Never a
   * point or a place: the low net is not determined until everybody has
   * finished (FR-062).
   */
  provisional_low_net: ProvisionalNet[]
}

export interface StrokeSheetPairing {
  match_id: number
  player_a: GoverningValues
  player_b: GoverningValues
}

export interface StrokeSheetDifference {
  player_id: string
  display_name: string
  reason: string
}

export interface CourseHole {
  hole_number: number
  par: number
  /** Canonical difficulty rank from the published scorecard (FR-008-E). */
  stroke_index: number
}

export interface CourseValues {
  course_key: string
  tee_name: string
  course_rating: Decimal
  slope_rating: number
  par: number
  /** How many holes the course description carries. */
  holes: number
  /** Per-hole par and stroke index — the reason a stroke falls where it does. */
  card: CourseHole[]
}

export interface StrokeSheet {
  event_id: number
  event: EventSummary
  /**
   * The course the sheet derives from. Course Handicap is
   * `Index × Slope/113 + (Rating − Par)`, so these ARE half the derivation —
   * a sheet showing the answer without them cannot be checked by hand.
   */
  course: CourseValues
  /** True until Start Round, and provisional ONLY as to the index (FR-024i). */
  provisional: boolean
  provisional_because?: string
  pairings: StrokeSheetPairing[]
  /** The bye player, who still plays and still receives strokes (FR-033). */
  unpaired: GoverningValues[]
  differences: StrokeSheetDifference[]
}

export interface PublicPlayer {
  public_id: string
  display_name: string
  /**
   * The readable name in a BROWSER path (`/players/dad`). Not an identifier —
   * every request in this client names a player by `public_id`. See
   * `components/playerPaths.tsx`.
   */
  slug: string
  active: boolean
  /** The roster carries each player's current index; null where they have none. */
  current_index?: Decimal | null
  rounds_in_index?: number
  /** Every season the player won, newest first -- on the roster only. */
  championships?: { season_id: number; season_name: string; decided_by: 'playoff' | 'points' }[]
}

export interface RoundSummary {
  scoring_round_id: number
  played_date: string
  course: string
  tee: string
  holes_played: number
  gross_score: number
  differential: Decimal
}

export interface PlayerProfile extends PublicPlayer {
  current_index: Decimal | null
  /** How many rounds the index is based on — four is not twenty. */
  rounds_in_index: number
  index_method?: string
  /**
   * League rounds grouped by length, each with its own average, best and worst.
   * A nine and an eighteen are never averaged or ranked together, and rounds
   * played outside the league (establishing rounds) are not counted.
   */
  by_length: {
    holes: 9 | 18
    rounds: number
    average: Decimal
    best: RoundSummary
    worst: RoundSummary
  }[]
  rounds_recorded: number
  season_standing: StandingRow | null
  /** The current season the standing belongs to; links to the player's season page. */
  season_id: number | null
  /** False for a season with no match play, where a W-L record says nothing. */
  season_has_matches: boolean
}

/** One player's season, week by week — the view the league's old software called Player Season Scores. */
export interface PlayerSeason {
  season_id: number
  season_name: string
  player: PublicPlayer
  /** The committed standing; null before any night is completed. */
  position: number | null
  points: Decimal | null
  /** The season's best-N cap; null when every round counts. */
  counting_rounds: number | null
  rounds_played: number
  rounds_counted: number
  has_matches: boolean
  weeks: PlayerWeek[]
}

export interface PlayerWeek {
  event: EventSummary
  /** A missed week is listed with its schedule and nothing else. */
  played: boolean
  playing_handicap: Decimal | null
  playing_handicap_overridden: boolean
  gross: number | null
  gross_to_par: number | null
  net: Decimal | null
  net_to_par: Decimal | null
  holes_completed: number
  finish_rank: number | null
  tied: boolean
  earned: string[]
  points: Decimal
  /** The night's points reach the season total. */
  counted: boolean
  /** The season's best-N cap left the night's points out. */
  dropped: boolean
}

export interface TrendPoint {
  effective_at: string
  index_value: Decimal
  rounds_counted: number
  method: string
  caused_by_round_id: number | null
}

export interface TrendComponent {
  differential: Decimal
  /** True for the lowest-N set, false for the rest of the window. */
  included: boolean
  source_kind: 'scoring_round' | 'differential_pair'
  round_ids: number[]
  /** The round, or the two nines a pair joined, oldest first. */
  rounds: ComponentRound[]
}

export interface ComponentRound {
  played_date: string
  course: string
  tee: string
  holes: number
  gross: number
  /** This round's own differential; a nine's is half-length. */
  differential: Decimal
  event_id?: number
}

export interface ComparedSeries {
  player_id: string
  display_name: string
  series: TrendPoint[]
}

export interface HandicapTrend {
  series: TrendPoint[]
  current_components: TrendComponent[]
  compare?: ComparedSeries[]
}

export interface HeadToHeadMatch {
  match_id: number
  event: EventSummary
  status: Match['status']
  winner_player_id: string | null
  margin: string | null
  outcome_note: string | null
}

export interface HeadToHead {
  player_id: string
  player_name: string
  opponent_id: string
  opponent_name: string
  wins: number
  losses: number
  halved: number
  matches: HeadToHeadMatch[]
}

/**
 * One hole of a live match.
 *
 * `outcome` alone cannot say why a hole scored nothing, because the engine
 * marks two different situations `pending` — a player has yet to hole out, and
 * the match was already decided. `contested` carries the engine's own answer
 * (see below), so this view never re-derives a scoring rule from the nets.
 */
export interface ScoreboardHole {
  hole: number
  a_net: Decimal | null
  b_net: Decimal | null
  /** `pending` while only one player has completed the hole (FR-035c). */
  outcome: 'a' | 'b' | 'halved' | 'pending'
  /** The running state after this hole, e.g. "AS" or "2UP A". */
  status_after: string
  /**
   * False for a hole that scored nothing: either the match was over before it
   * (FR-030, FR-035h) or somebody has yet to finish it. `settled` on the match
   * says which.
   */
  contested: boolean
}

export interface ScoreboardMatch {
  match_id: number
  /** Golf's own phrasing, from the server: "Steve 2 up", "all square". */
  status_text: string
  player_a_id: string
  player_b_id: string
  player_a_name: string
  player_b_name: string
  /** Zero once settled: no further hole is scored in it (FR-035h). */
  holes_remaining: number
  settled: boolean
  margin: string | null
  winner_player_id: string | null
  holes: ScoreboardHole[]
}

/** The running net, and how much golf it is the sum of (FR-035d). */
export interface ProvisionalNet {
  player_id: string
  display_name: string
  net_so_far: Decimal
  /**
   * Net against the par of the holes this player has finished. The rows arrive
   * ORDERED by it: a total rewards whoever has played least, a figure against
   * par does not.
   */
  net_to_par: Decimal
  holes_completed: number
  /**
   * Gross and the handicap, so a round being played reads the same way as one
   * that has been scored (`Placing`). Null where no hole has been recorded —
   * a zero would read as a round of nothing rather than one not played.
   */
  gross: number | null
  gross_to_par: number | null
  /** The strokes received on the holes played — what the round is played off. */
  playing_handicap: Decimal | null
  playing_handicap_overridden: boolean
}

export interface Scoreboard {
  event_id: number
  /** When the scoreboard last actually changed — never absent (FR-035e). */
  updated_at: string
  /** True when nothing has arrived recently; the state below is still real. */
  stale: boolean
  event_state: EventState
  matches: ScoreboardMatch[]
  provisional_low_net: ProvisionalNet[]
}

export interface ProjectedMatch {
  match_id: number
  player_a_id: string
  player_b_id: string
  player_a_name: string
  player_b_name: string
  status_text: string
  /** No remaining hole can change this one — it is a result, not a guess. */
  already_settled: boolean
  winner_player_id: string | null
  halved: boolean
  /** Which tie-break tier decided it, where one was needed. */
  decided_by?: string
  holes_remaining: number
}

export interface ProjectedResult {
  player_id: string
  display_name: string
  result_type: string
  match_id: number | null
  value: Decimal | null
  finish_rank: number | null
  tied: boolean
  points: Decimal
}

export interface EventProjection {
  /** So a view can stop asking: `recorded` is only true at `complete`. */
  state: EventState
  event_id: number
  /**
   * The holes EVERY player has completed. The low net is computed over exactly
   * these, because ranking a partial field on total net puts whoever has played
   * fewest holes on top.
   */
  through_holes: number[]
  /** True once every player has a full card — then this IS what completing records. */
  complete: boolean
  /** The round has been completed, so this is what it recorded — not a projection. */
  recorded: boolean
  matches: ProjectedMatch[]
  results: ProjectedResult[]
  /** What tonight would be worth, one row per player. */
  placings: Placing[]
  /** What cannot be projected yet, so a view says why rather than showing a blank. */
  undetermined: string[]
}

export interface ProjectedStandingRow {
  player_id: string
  display_name: string
  committed_points: Decimal
  projected_points: Decimal
  projected_total: Decimal
  projected_position: number
  committed_position: number
  matches_won: number
  matches_lost: number
  matches_halved: number
}

export interface Projection {
  /** Always true, and a constant in the contract: a client cannot render this
   * without knowing what it is (FR-061). */
  projected: boolean
  committed_through: number | null
  event: EventProjection
  in_progress: EventSummary[]
  rows: ProjectedStandingRow[]
  /** False for a season with no match play; the record column is left out. */
  has_matches: boolean
}

export interface Announcement {
  id: number
  title: string
  body: string
  posted_at: string
}

/**
 * The problem body every route answers a failure with.
 *
 * `failed_checks` appears on a course validation failure and nowhere else. It
 * names the invariants that broke, because "rejected whole" is only actionable
 * if the submitter is told WHICH one — a course rejected without saying whether
 * the stroke indexes are not a permutation or the pars do not sum leaves
 * somebody re-typing eighteen numbers at random.
 */
export interface ApiProblem {
  error: string
  detail?: string
  failed_checks?: string[]
  request_id?: string
}

/** ApiError carries the status and the request id, which is what a bug report needs. */
export class ApiError extends Error {
  // Declared rather than written as constructor parameter properties, which
  // `erasableSyntaxOnly` forbids: that syntax emits code, and the build is
  // configured so TypeScript only ever erases.
  readonly status: number
  readonly problem: ApiProblem

  constructor(status: number, problem: ApiProblem) {
    super(problem.detail || problem.error || `HTTP ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
  }
}


// --- the playoff bracket (FR-035, FR-063) ----------------------------------

/**
 * How one side of a bracket match was determined. Exactly one key is present.
 *
 * Carried on the match itself rather than only in the format, so a bracket is
 * explainable from what it holds: "seed 1 v seed 4" says why those two are
 * playing, and "the winner of round 1 slot 1" says what an empty slot is
 * waiting for.
 */
export interface BracketSource {
  seed?: number
  winner_of?: { round: number; slot: number }
}

/**
 * One player's place in a seeding, qualified or not.
 *
 * **Every player appears, including those below the cut.** A cut recorded as an
 * absence cannot be questioned: in 2025-26 Dad is seed 5, level with Shawn on
 * points and behind on the match record, and that is exactly the fact a reader
 * wants.
 */
export interface BracketSeed {
  seed: number
  player_id: string
  display_name: string
  points: Decimal
  matches_won: number
  matches_lost: number
  matches_halved: number
  qualified: boolean
  /**
   * Which tier separated this player from an adjacent one when points did not
   * — `match_wins` or `head_to_head`. Absent when points decided it.
   */
  tiebreak_applied?: string
}

export interface BracketMatch {
  slot: number
  source_a: BracketSource
  source_b: BracketSource
  /** Null while the slot waits on an earlier round: a match names two people. */
  match_id: number | null
  player_a_id: string | null
  player_b_id: string | null
  player_a_name?: string
  player_b_name?: string
  winner_player_id: string | null
  /** Golf's own notation for a played match, e.g. "2 up" or "3&2". */
  margin: string | null
  status?: string
}

export interface BracketRound {
  round: number
  event_id: number
  event: EventSummary
  matches: BracketMatch[]
}

export interface Bracket {
  qualifiers: number
  seeded_at: string
  /** The order AS APPLIED, not the season's current one (FR-027e). */
  seeding_rule: { order: string[] }
  seeds: BracketSeed[]
  rounds: BracketRound[]
  /** Derived from the highest round; null until there is one. */
  champion_player_id: string | null
  undetermined: string[]
}

export interface ProjectedStrokeRow {
  player_id: string
  display_name: string
  handicap_index: Decimal
  course_handicap: number
  playing_handicap: Decimal
  handicap_adjustment: Decimal
  adjusted_playing_handicap: Decimal
  strokes_received: Decimal
  holes_played: number
}

export interface ProjectedStrokes {
  allowance_pct: Decimal
  players: ProjectedStrokeRow[]
}

export interface ProjectedMatchup {
  round: number
  slot: number
  event_id: number | null
  source_a: BracketSource
  source_b: BracketSource
  player_a_id: string | null
  player_b_id: string | null
  course: string | null
  tee: string | null
  /** Null for a slot fed by an earlier round, and for a week with no venue. */
  strokes: ProjectedStrokes | null
}

export interface BracketProjection {
  /** Always true. A client cannot render this without knowing what it is. */
  projected: boolean
  as_of: string
  /** How many counting events are still to play — how provisional this is. */
  events_remaining: number
  qualifiers: number
  seeding_rule: { order: string[] }
  seeds: BracketSeed[]
  matchups: ProjectedMatchup[]
  undetermined: string[]
}


// --- statistics (FR-039d through FR-039i) ----------------------------------

/** Every metric the server computes, in presentation order. */
export const METRICS = [
  'score_history',
  'score_distribution',
  'score_distribution_by_player',
  'scoring_average',
  'best_round',
  'worst_round',
  'potential_best_round',
  'putting_average',
  'putting_distribution',
  'par3',
  'par4',
  'par5',
  'hole_difficulty',
  'fairways',
  'greens_in_regulation',
  'putts',
  'sand_saves',
  'driving_distance',
] as const

export type Metric = (typeof METRICS)[number]

/**
 * The metrics that need no observed value at all (FR-039p).
 *
 * A score records how many strokes, never where the ball went. These work from
 * the gross and the par already on the hole, which is why they cover the
 * league's whole record while the observed ones cover a fraction of it.
 */
export const DERIVED_METRICS: readonly Metric[] = [
  'score_history',
  'score_distribution',
  'score_distribution_by_player',
  'scoring_average',
  'best_round',
  'worst_round',
  'potential_best_round',
  'par3',
  'par4',
  'par5',
  'hole_difficulty',
]

export interface StatBucket {
  key: string
  label: string
  count: number
  value: Decimal | null
  /** A round's score against the par of the holes it played; only on a bucket that is one round. */
  to_par?: Decimal
  event_id?: number
  player_id?: string
  display_name?: string
  course?: string
  date?: string
}

export interface StatSeries {
  player_id: string
  display_name: string
  based_on_rounds: number
  based_on_holes: number
  no_data: boolean
  breakdown: StatBucket[]
}

export interface StatResult {
  metric: string
  basis: 'net' | 'gross'
  /**
   * Always present (FR-039r). An aggregate is never shown as covering every
   * round when the measure is only sometimes recorded — across the league's
   * 115 scored rounds, putts appear on 109 and sand saves on 17.
   */
  based_on_rounds: number
  based_on_holes: number
  rounds_in_period: number
  holes_in_period: number
  complete_coverage: boolean
  /** True when the measure has nothing for this period. Mark it; never render a zero. */
  no_data: boolean
  value: Decimal | null
  breakdown: StatBucket[]
  /**
   * Each player's breakdown, for a metric that compares players, with that
   * player's own coverage. Empty for every other metric.
   */
  series: StatSeries[]
  /**
   * What can be filtered to WITHIN THIS PERIOD (FR-039h).
   *
   * From the holes the period holds, not from the roster — so a control never
   * offers a slice that leads to "nothing recorded".
   */
  available: {
    players: { player_id: string; display_name: string }[]
    courses: string[]
  }
}

export interface StatLeader {
  metric: string
  player_id: string
  display_name: string
  value: Decimal
  event_id?: number
  course?: string
  week_number?: number
  tied_with: string[]
  /** The metric is real and nobody has achieved it yet — a zero, not a gap. */
  nobody: boolean
}

export interface StatQuery {
  metric: Metric
  basis?: 'net' | 'gross'
  seasonId?: number
  playerId?: string
  courseKey?: string
  eventId?: number
}

/**
 * Where a member goes to see one player's numbers (FR-039h).
 *
 * **Takes whatever names the player in a LINK, which is their slug.** Stats
 * resolves either form, so passing a public id still works — but every link the
 * app writes should carry the readable one, in a query exactly as in a path.
 */
export function statsHref(player: string, metric: Metric = 'score_distribution'): string {
  return `/stats?metric=${metric}&player_id=${encodeURIComponent(player)}`
}

/**
 * The public group's prefix.
 *
 * `/api/v1`, matching the contract's declared server. In development Vite
 * proxies it to the API on :8080 so the browser sees one origin; in production
 * nginx does the same. **The client never learns an API host**, which is what
 * lets the bundle be deployed without knowing where it will be served from.
 */
export const PUBLIC_PREFIX = '/api/v1'

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(PUBLIC_PREFIX + path, {
    signal,
    headers: { Accept: 'application/json' },
    // **No credentials.** Every read here is public and unauthenticated
    // (FR-047a); sending a session cookie to a route that ignores it would be
    // a habit worth not forming.
    credentials: 'omit',
  })
  if (!res.ok) {
    let problem: ApiProblem = { error: `http_${res.status}` }
    try {
      problem = (await res.json()) as ApiProblem
    } catch {
      // A non-JSON body from a failing route is not itself worth reporting.
    }
    throw new ApiError(res.status, problem)
  }
  return (await res.json()) as T
}

export const api = {
  seasons: (s?: AbortSignal) => get<Season[]>('/seasons', s),
  schedule: (seasonId: number, s?: AbortSignal) =>
    get<EventSummary[]>(`/seasons/${seasonId}/schedule`, s),

  /**
   * Events currently accepting holes, with the one being played marked
   * (FR-077). Read by the console to say where a producer's holes will land.
   */
  openEvents: (signal?: AbortSignal) => get<OpenEvent[]>('/events/open', signal),
  standings: (seasonId: number, s?: AbortSignal) =>
    get<Standings>(`/seasons/${seasonId}/standings`, s),
  // Two routes, never one with a flag. A projected seeding must never be
  // presentable as a settled one, and a client that has to call a different
  // URL cannot render one as the other by accident (FR-063).
  bracket: (seasonId: number, s?: AbortSignal) =>
    get<Bracket>(`/seasons/${seasonId}/bracket`, s),
  bracketProjection: (seasonId: number, s?: AbortSignal) =>
    get<BracketProjection>(`/seasons/${seasonId}/bracket/projection`, s),
  event: (eventId: number, s?: AbortSignal) => get<EventDetail>(`/events/${eventId}`, s),
  strokeSheet: (eventId: number, s?: AbortSignal) =>
    get<StrokeSheet>(`/events/${eventId}/stroke-sheet`, s),
  projection: (eventId: number, s?: AbortSignal) =>
    get<Projection>(`/events/${eventId}/projection`, s),
  players: (s?: AbortSignal) => get<PublicPlayer[]>('/players', s),
  player: (playerId: string, s?: AbortSignal) => get<PlayerProfile>(`/players/${playerId}`, s),
  playerSeason: (seasonId: number | string, playerId: string, s?: AbortSignal) =>
    get<PlayerSeason>(`/seasons/${seasonId}/players/${playerId}`, s),
  handicapTrend: (playerId: string, compareWith: string[] = [], s?: AbortSignal) => {
    const query = compareWith.map((id) => `compare_with=${encodeURIComponent(id)}`).join('&')
    return get<HandicapTrend>(
      `/players/${playerId}/handicap-trend${query ? `?${query}` : ''}`,
      s,
    )
  },
  headToHead: (playerId: string, opponentId: string, s?: AbortSignal) =>
    get<HeadToHead>(`/players/${playerId}/head-to-head/${opponentId}`, s),
  announcements: (s?: AbortSignal) => get<Announcement[]>('/announcements', s),
  stats: (q: StatQuery, s?: AbortSignal) => {
    const params = new URLSearchParams({ metric: q.metric })
    if (q.basis) params.set('basis', q.basis)
    if (q.seasonId) params.set('season_id', String(q.seasonId))
    if (q.playerId) params.set('player_id', q.playerId)
    if (q.courseKey) params.set('course_key', q.courseKey)
    if (q.eventId) params.set('event_id', String(q.eventId))
    return get<StatResult>(`/stats?${params.toString()}`, s)
  },
  leaders: (seasonId: number, s?: AbortSignal) =>
    get<StatLeader[]>(`/seasons/${seasonId}/leaders`, s),
}
